import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";
import { Users, Clock, MessageCircle, RefreshCw, Smartphone } from "lucide-react";
import dashboard1 from "@/assets/analytics-dashboard-1.jpg";
import { Badge } from "@/components/ui/badge";

const metrics = [
  { icon: Users, label: "Generierte Kontakte", color: "text-chathero-mint" },
  { icon: Clock, label: "Assistentensitzungen & Ø Sitzungsdauer", color: "text-chathero-orange" },
  { icon: MessageCircle, label: "Gesamtinteraktionen", color: "text-primary" },
  { icon: RefreshCw, label: "Wiederkehrende Interaktionen", color: "text-chathero-mint" },
  { icon: Smartphone, label: "Kanalverteilung", color: "text-chathero-orange" },
];

const kpis = [
  { value: "45", label: "neue Kontakte", color: "text-primary" },
  { value: "396", label: "Sitzungen", color: "text-chathero-mint" },
  { value: "821", label: "Interaktionen", color: "text-chathero-orange" },
];

const AnalyticsInteraktionenSection = () => {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className={cn(
        "py-16 md:py-24 transition-all duration-700 ease-out",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      )}
    >
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Badge variant="outline" className="mb-4 text-sm font-semibold text-primary uppercase tracking-wider">Interaktionen</Badge>
            <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4">
              Interaktionen messbar machen
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Sie sehen auf einen Blick, welche Touchpoints Wachstum treiben – und wo Potenzial ungenutzt bleibt.
            </p>

            <div className="space-y-3 mb-8">
              {metrics.map((metric, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-secondary/40 border border-border">
                  <metric.icon className={cn("w-5 h-5 shrink-0", metric.color)} />
                  <span className="text-sm text-foreground">{metric.label}</span>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-3 gap-4">
              {kpis.map((kpi, i) => (
                <div key={i} className="bg-card border border-border rounded-xl p-4 text-center shadow-sm">
                  <p className={cn("text-3xl font-bold", kpi.color)}>{kpi.value}</p>
                  <p className="text-xs text-muted-foreground mt-1">{kpi.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Dashboard Screenshot */}
          <div className="rounded-2xl overflow-hidden shadow-2xl border border-border relative">
            <img
              src={dashboard1}
              alt="Chathero AI Analytics Dashboard – Interaktionen und Sitzungsübersicht"
              className="w-full h-[360px] object-cover object-top"
              loading="lazy"
            />
            <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-background to-transparent" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default AnalyticsInteraktionenSection;
